import { Component, ChangeDetectorRef, ChangeDetectionStrategy, inject, model, OnInit, ViewChild } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { SelectionModel } from '@angular/cdk/collections';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

// Angular Material
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatStepperModule } from '@angular/material/stepper';
import { provideNativeDateAdapter } from '@angular/material/core';
import
{
  MatDialog,
  MatDialogContent,
  MatDialogTitle,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';

// Services and Utils
import { GuideClaimColumns, TableColumn, CommonService } from '../../../services/common-service';
import { DatatableComponent } from '../../../utils/datatable-component/datatable-component';
import { GuideParticipant, GuideInvoice, GuideReceived, GuideRemitted } from '../../../models/billing';
import { BillingService } from '../../../services/billing-service';
import { NotificationService } from '../../../services/notification-service';



@Component( {
  selector: 'app-guide-manage-claims',
  imports: [
    TranslatePipe,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatButtonModule,
    MatStepperModule,
    DatatableComponent,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './guide-manage-claims.html',
  styleUrl: './guide-manage-claims.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
} )
export class GuideManageClaims implements OnInit
{

  constructor(
    private _billingService: BillingService,
    private _notificationService: NotificationService,
    private _commonService: CommonService,
    private _cdr: ChangeDetectorRef,
  ) { }


  @ViewChild( 'invoiceTable' ) invoiceTable!: DatatableComponent;
  @ViewChild( 'receivedTable' ) receivedTable!: DatatableComponent;
  @ViewChild( 'remitedTable' ) remitedTable!: DatatableComponent;

  dialog = inject( MatDialog );

  public filters = {
    serviceDateFrom: new Date( new Date().setFullYear( new Date().getFullYear() - 1 ) ),
    serviceDateTo: new Date()
  }
  public invoiceColumns: TableColumn[] = GuideClaimColumns['Invoice'].DefaultColumns.filter( c => !!c.field ) || [];
  public receivedColumns: TableColumn[] = GuideClaimColumns['Received'].DefaultColumns.filter( c => !!c.field ) || [];
  public remittedColumns: TableColumn[] = GuideClaimColumns['Remitted'].DefaultColumns.filter( c => !!c.field ) || [];
  public invoiceData = model<GuideInvoice[]>( [] );
  public receivedData = model<GuideReceived[]>( [] );
  public remitedData = model<any[]>( [] );
  public invoiceSelection: SelectionModel<GuideInvoice> = new SelectionModel<GuideInvoice>( true, [] );
  public receivedSelection: SelectionModel<GuideReceived> = new SelectionModel<GuideReceived>( true, [] );
  public remitSelection: SelectionModel<GuideRemitted> = new SelectionModel<GuideRemitted>( true, [] );
  public participants: GuideParticipant[] = [];
  public selectedParticipant: GuideParticipant | null = null;
  public stepIndex = 0;


  public invoiceSummary = () =>
  {
    var claimsCount = 0;
    var claimsTotal = 0;
    var status = "processing...";
    switch ( this.stepIndex )
    {
      case 0:
        claimsCount = this.invoiceSelection.selected.length;
        claimsTotal = this.invoiceSelection.selected.reduce( ( sum, claim ) => sum + ( claim.ccsInvoiceDueAmt || 0 ), 0 );
        status = "Generating invoice...";        
        break;
      case 1:
        claimsCount = this.receivedSelection.selected.length;
        claimsTotal = this.receivedSelection.selected.reduce( ( sum, claim ) => sum + ( claim.receivedAmount || 0 ), 0 );
        status = "Recieving payment...";   
        break;
      case 2:
        claimsCount = this.remitSelection.selected.length;
        claimsTotal = this.remitSelection.selected.reduce( ( sum, claim ) => sum + ( claim.ccsReceivedAmount || 0 ), 0 );
        status = "Remiting payment..."; 
        break;
      default:
        break;
    }

    return {
      totalCount: claimsCount,
      totalAmount: claimsTotal,     
      participant: this.selectedParticipant?.name,
      status: status,
      comment:'This may take a couple of mins...',
    }
  };

  async ngOnInit(): Promise<void>
  {
    this.participants = await this._billingService.getGuideParticipants( {} ) || [];
    this.selectedParticipant = this.participants[0] ?? null;
    await this.applyFilters();
    this.invoiceSelection = this.invoiceTable.selection;
    this.receivedSelection = this.receivedTable.selection;
    this.remitSelection = this.remitedTable.selection;
    this._cdr.detectChanges();
  }

  async onStepChanged( event: any ): Promise<void>
  {
    this.stepIndex = event.selectedIndex;
    this.applyFilters();
  }

  async applyFilters(): Promise<void>
  {
    switch ( this.stepIndex )
    {
      case 0:
        await this.applyInvoicesFilters();
        return;
      case 1:
        await this.applyRevievedFilters()
        return;
      case 2:
        await this.applyRemitedFilters();
        return;
    }
  }

  async applyInvoicesFilters(): Promise<void>
  {
    var filtersToApply: any = {
      participantId: this.selectedParticipant?.id || "",
      serviceDateFrom: this.filters.serviceDateFrom ? this.filters.serviceDateFrom.toLocaleDateString() : "",
      serviceDateTo: this.filters.serviceDateTo ? this.filters.serviceDateTo.toLocaleDateString() : "",
    };
    const invoices = await this._billingService.getGuideInvoices( filtersToApply ) || [];
    this.invoiceData.update( ( current ) => { current = invoices; return current } );
    this.invoiceTable.data = this.invoiceData;
    this.invoiceTable.pageSize = 100;
    this.invoiceTable.ngOnInit();
    this._cdr.detectChanges();
  }

  async applyRevievedFilters(): Promise<void>
  {
    var filtersToApply: any = {
      participantId: this.selectedParticipant?.id || "",
      serviceDateFrom: this.filters.serviceDateFrom ? this.filters.serviceDateFrom.toLocaleDateString() : "",
      serviceDateTo: this.filters.serviceDateTo ? this.filters.serviceDateTo.toLocaleDateString() : "",
    };
    const received = await this._billingService.getGuideReceived( filtersToApply ) || [];
    received.filter( x => x.eventType.toUpperCase() === 'RESPITE' ).forEach( r => r.isEditable = true );
    this.receivedData.update( ( current ) => { current = received; return current } );

    this.receivedTable.data = this.receivedData;
    this.receivedTable.ngOnInit();
    this._cdr.detectChanges();
  }

  async applyRemitedFilters(): Promise<void>
  {
    var filtersToApply: any = {
      participantId: this.selectedParticipant?.id || "",
      serviceDateFrom: this.filters.serviceDateFrom ? this.filters.serviceDateFrom.toLocaleDateString() : "",
      serviceDateTo: this.filters.serviceDateTo ? this.filters.serviceDateTo.toLocaleDateString() : "",
    };
    const remited = await this._billingService.getGuideRemited( filtersToApply ) || [];
    this.remitedData.update( ( current ) => { current = remited; return current } );
    this.remitedTable.data = this.remitedData;
    this.remitedTable.ngOnInit();
    this._cdr.detectChanges();
  }

  async clearSelection(): Promise<void>
  {
    switch ( this.stepIndex )
    {
      case 0:
        await this.invoiceSelection.clear();
        return;
      case 1:
        await this.receivedSelection.clear();
        return;
      case 2:
        await this.remitSelection.clear();
        return;
    }
  }

  async generateInvoiceCVS(): Promise<void>
  {
    // Get selected invoices
    const selectedInvoices = this.invoiceSelection.selected;
    if ( selectedInvoices.length === 0 )
    {
      this._notificationService.showErrorMessages( ['No invoices selected', 'Please select at least one invoice to generate the CSV file.'] );
      return;
    };
    try
    {
      // Generate CSV content
      const csvContent = await this._commonService.FileHelper.convertToCsv( selectedInvoices );
      // Trigger file download
      this._commonService.FileHelper.downloadFile( csvContent, `guide_invoices_${Date.now()}.csv`, 'text/csv' );
    }
    catch ( error: any )
    {
      this._notificationService.showErrorMessages( ['Error generating CSV', error.message || error] );
    }
  }

  async openDialog(): Promise<void>
  {
    this.dialog.open( GuideManageClaimsProcessingDialog, {
      data: this.invoiceSummary(),
      disableClose: true,
    } );
  }

  async closeDialog(): Promise<void>
  {
    this.dialog.closeAll();
  }

  async generateInvoice(): Promise<void>
  {
    // Get selected invoices
    const selectedInvoices = this.invoiceSelection.selected;
    if ( selectedInvoices.length === 0 )
    {
      this._notificationService.showErrorMessages( ['No invoices selected', 'Please select at least one invoice to generate the invoice.'] );
      return;
    };
    if ( !this.selectedParticipant )
    {
      this._notificationService.showErrorMessages( ['No participant selected', 'Please select a participant to generate the invoice.'] );
      return;
    };

    await this.openDialog();

    try
    {
      // Generate invoice
      const invoiceResult = await this._billingService.generateInvoice( selectedInvoices, this.selectedParticipant );
      this._notificationService.showSuccessMessages( invoiceResult! );
      this.applyFilters();
      this._cdr.detectChanges();
    }
    catch ( error: any )
    {
      this._notificationService.showErrorMessages( ['Error generating invoice', error.message || error] );
    }
    finally
    {
      await this.closeDialog();
    }
  }

  async receiveInvoices(): Promise<void>
  {
    // Get selected invoices
    const selectedInvoices = this.receivedSelection.selected;
    if ( selectedInvoices.length === 0 )
    {
      this._notificationService.showErrorMessages( ['No invoices selected', 'Please select at least one invoice to receive the invoices.'] );
      return;
    };

    await this.openDialog();
    try
    {
      // Receive invoices
      const receiveResult = await this._billingService.receiveInvoices( selectedInvoices );
      this._notificationService.showSuccessMessages( receiveResult! );
      this.applyFilters();
      this._cdr.detectChanges();
    }
    catch ( error: any )
    {

      this._notificationService.showErrorMessages( ['Error receiving invoices', error.message || error] );
    }
    finally
    {
      await this.closeDialog();
    }
  }

  async remitInvoices(): Promise<void>
  {
    await this.openDialog();

    // Get selected invoices
    const selectedInvoices = this.remitSelection.selected;
    if ( selectedInvoices.length === 0 )
    {
      this._notificationService.showErrorMessages( ['No invoices selected', 'Please select at least one invoice to receive the invoices.'] );
      return;
    };

    await this.openDialog();
    try
    {
      // Remit invoices
      const remitResult = await this._billingService.remitInvoices( selectedInvoices );
      this._notificationService.showSuccessMessages( remitResult! );
      this.applyFilters();
      this._cdr.detectChanges();
    }
    catch ( error: any )
    {
      this._notificationService.showErrorMessages( ['Error remitting invoices', error.message || error] );
    }
    finally
    {
      await this.closeDialog();
    }
  }

}


@Component( {
  selector: 'guide-manage-claims-invoice-summary',
  templateUrl: 'guide-manage-claims-invoice-summary.html',
  imports: [MatDialogTitle, MatDialogContent,CurrencyPipe, MatProgressBarModule],
} )
export class GuideManageClaimsProcessingDialog
{
  data = inject( MAT_DIALOG_DATA );
}
